import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDistributionType } from "./enums/enumDistributionType.generated";
import { EnumEcommerceConnectionStatus } from "./enums/enumEcommerceConnectionStatus.generated";
import { EnumEcommerceOrderOriginType } from "./enums/enumEcommerceOrderOriginType.generated";

// Model Objects

export class EcommerceConnectionOutletMappingReadOnly extends ModelObject<EcommerceConnectionOutletMappingReadOnly> {

    /**
    * The ecommerce connection settings which this mapping belongs to.
    */
    public ecommerceConnectionSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ecommerceConnectionSettingsId = Guid.parseOrEmpty(jsonSerializedObject.ecommerceConnectionSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ecommerceConnectionSettingsId = this.ecommerceConnectionSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ecommerceConnectionSettingsId", rule: (v: any) => !v.isEmpty() || "EcommerceConnectionOutletMappingReadOnly.EcommerceConnectionSettingsId cannot be empty"},
    ];
}


export class EcommerceConnectionOutletMapping extends ModelObject<EcommerceConnectionOutletMapping> {

    /**
    * The location name as given by the origin.
    * Not Nullable
    */
    public originLocationName: string = "";
    /**
    * The Brewman outlet id to use for orders at the given ecommerce location.
    */
    public brewmanOutletId: Guid = Guid.createEmpty();
    public readOnly: EcommerceConnectionOutletMappingReadOnly = new EcommerceConnectionOutletMappingReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.originLocationName = jsonSerializedObject.originLocationName;
          this.brewmanOutletId = Guid.parseOrEmpty(jsonSerializedObject.brewmanOutletId);
          this.readOnly = new EcommerceConnectionOutletMappingReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.originLocationName = this.originLocationName;
        toRet.brewmanOutletId = this.brewmanOutletId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "brewmanOutletId", rule: (v: any) => !v.isEmpty() || "EcommerceConnectionOutletMapping.BrewmanOutletId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class EcommerceConnectionShippingMappingReadOnly extends ModelObject<EcommerceConnectionShippingMappingReadOnly> {

    /**
    * The ecommerce connection settings which this mapping belongs to.
    */
    public ecommerceConnectionSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ecommerceConnectionSettingsId = Guid.parseOrEmpty(jsonSerializedObject.ecommerceConnectionSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ecommerceConnectionSettingsId = this.ecommerceConnectionSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ecommerceConnectionSettingsId", rule: (v: any) => !v.isEmpty() || "EcommerceConnectionShippingMappingReadOnly.EcommerceConnectionSettingsId cannot be empty"},
    ];
}


export class EcommerceConnectionShippingMapping extends ModelObject<EcommerceConnectionShippingMapping> {

    /**
    * The shipping method name as given by the origin.
    * Not Nullable
    */
    public originShippingMethodName: string = "";
    /**
    * The Brewman distribution type to use for orders with this shipping method.
    */
    public distributionType: EnumDistributionType = EnumDistributionType.none;
    private _deliveryAreaId: Guid | null = null;
    /**
    * Delivery Area Id, required if the distribution type is delivery.
    */
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    private _courierId: Guid | null = null;
    /**
    * Courier Id, required if the distribution type is courier.
    */
    public get courierId(): Guid | null { return this._courierId }
    public set courierId(value: Guid | null) {
      this._courierId = value === undefined ? null : value;
    }
    public readOnly: EcommerceConnectionShippingMappingReadOnly = new EcommerceConnectionShippingMappingReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.originShippingMethodName = jsonSerializedObject.originShippingMethodName;
          this.distributionType = EnumDistributionType.getByValue(jsonSerializedObject.distributionType, this.distributionType);
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.courierId = jsonSerializedObject.courierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.courierId);
          this.readOnly = new EcommerceConnectionShippingMappingReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.originShippingMethodName = this.originShippingMethodName;
        toRet.distributionType = this.distributionType.value;
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.courierId = this.courierId == null ? null : this.courierId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceConnectionShippingMapping.DeliveryAreaId cannot be empty"},
            {field: "courierId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceConnectionShippingMapping.CourierId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class EcommerceConnectionSettingsReadOnly extends ModelObject<EcommerceConnectionSettingsReadOnly> {

    /**
    * The ecommerce store identifier.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public originStoreIdentifier: string = "";
    /**
    * The origin type of this order.
    */
    public originType: EnumEcommerceOrderOriginType = EnumEcommerceOrderOriginType.none;
    public status: EnumEcommerceConnectionStatus = EnumEcommerceConnectionStatus.pendingAuthorisation;
    public authenticationSetupUrl: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.originStoreIdentifier = jsonSerializedObject.originStoreIdentifier;
          this.originType = EnumEcommerceOrderOriginType.getByValue(jsonSerializedObject.originType, this.originType);
          this.status = EnumEcommerceConnectionStatus.getByValue(jsonSerializedObject.status, this.status);
          this.authenticationSetupUrl = jsonSerializedObject.authenticationSetupUrl;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.originStoreIdentifier = this.originStoreIdentifier;
        toRet.originType = this.originType.value;
        toRet.status = this.status.value;
        toRet.authenticationSetupUrl = this.authenticationSetupUrl;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class EcommerceConnectionSettings extends ModelObject<EcommerceConnectionSettings> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on the ecommerce connection from before this Date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    private _miscProductId: Guid | null = null;
    /**
    * Identifies the product to be used for items tagged as AllowBrewManMiscellaneous.
    */
    public get miscProductId(): Guid | null { return this._miscProductId }
    public set miscProductId(value: Guid | null) {
      this._miscProductId = value === undefined ? null : value;
    }
    /**
    * Whether we include archive orders when importing from Shopify
    */
    public allowImportArchiveOrders: boolean = false;
    private _priceListId: Guid | null = null;
    /**
    * PriceList Id. If provided then Products on the ecommerce connection will be updated with the List Price in BrewMan.
    */
    public get priceListId(): Guid | null { return this._priceListId }
    public set priceListId(value: Guid | null) {
      this._priceListId = value === undefined ? null : value;
    }
    /**
    * Whether BrewMan's stock levels should be sent to the ecommerce connection. Will disable stock level pushing for integrations that can match on stock location naming.
    */
    public sendStockLevels: boolean = false;
    private _stockLevelsStockLocationId: Guid | null = null;
    /**
    * Stock Location Id. If provided then stock levels on the ecommerce connection will always be updated with the stock levels from this stock location in BrewMan.
    */
    public get stockLevelsStockLocationId(): Guid | null { return this._stockLevelsStockLocationId }
    public set stockLevelsStockLocationId(value: Guid | null) {
      this._stockLevelsStockLocationId = value === undefined ? null : value;
    }
    private _fallbackDeliveryServiceId: Guid | null = null;
    /**
    * Fallback delivery service stock item to use for delivery charges if a delivery charge cannot be matched on name.
    */
    public get fallbackDeliveryServiceId(): Guid | null { return this._fallbackDeliveryServiceId }
    public set fallbackDeliveryServiceId(value: Guid | null) {
      this._fallbackDeliveryServiceId = value === undefined ? null : value;
    }
    public outletMappings: (EcommerceConnectionOutletMapping)[] = [];
    private _fallbackOutletId: Guid | null = null;
    /**
    * Outlet Id to use when an outlet mapping cannot be resolved.
    */
    public get fallbackOutletId(): Guid | null { return this._fallbackOutletId }
    public set fallbackOutletId(value: Guid | null) {
      this._fallbackOutletId = value === undefined ? null : value;
    }
    public shippingMappings: (EcommerceConnectionShippingMapping)[] = [];
    public readOnly: EcommerceConnectionSettingsReadOnly = new EcommerceConnectionSettingsReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.miscProductId = jsonSerializedObject.miscProductId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.miscProductId);
          this.allowImportArchiveOrders = jsonSerializedObject.allowImportArchiveOrders;
          this.priceListId = jsonSerializedObject.priceListId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.sendStockLevels = jsonSerializedObject.sendStockLevels;
          this.stockLevelsStockLocationId = jsonSerializedObject.stockLevelsStockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLevelsStockLocationId);
          this.fallbackDeliveryServiceId = jsonSerializedObject.fallbackDeliveryServiceId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.fallbackDeliveryServiceId);
          this.outletMappings = jsonSerializedObject.outletMappings.map((v: any) => new EcommerceConnectionOutletMapping(v));
          this.fallbackOutletId = jsonSerializedObject.fallbackOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.fallbackOutletId);
          this.shippingMappings = jsonSerializedObject.shippingMappings.map((v: any) => new EcommerceConnectionShippingMapping(v));
          this.readOnly = new EcommerceConnectionSettingsReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.miscProductId = this.miscProductId == null ? null : this.miscProductId.toString();
        toRet.allowImportArchiveOrders = this.allowImportArchiveOrders;
        toRet.priceListId = this.priceListId == null ? null : this.priceListId.toString();
        toRet.sendStockLevels = this.sendStockLevels;
        toRet.stockLevelsStockLocationId = this.stockLevelsStockLocationId == null ? null : this.stockLevelsStockLocationId.toString();
        toRet.fallbackDeliveryServiceId = this.fallbackDeliveryServiceId == null ? null : this.fallbackDeliveryServiceId.toString();
        toRet.outletMappings = this.outletMappings.map(v => v.toJsonSerializedObject());
        toRet.fallbackOutletId = this.fallbackOutletId == null ? null : this.fallbackOutletId.toString();
        toRet.shippingMappings = this.shippingMappings.map(v => v.toJsonSerializedObject());
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "EcommerceConnectionSettings.Id cannot be empty"},
            {field: "miscProductId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceConnectionSettings.MiscProductId cannot be empty"},
            {field: "priceListId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceConnectionSettings.PriceListId cannot be empty"},
            {field: "stockLevelsStockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceConnectionSettings.StockLevelsStockLocationId cannot be empty"},
            {field: "fallbackDeliveryServiceId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceConnectionSettings.FallbackDeliveryServiceId cannot be empty"},
            {field: "outletMappings", rule: (v: any) => !!v || "outletMappings is required"},
            {field: "outletMappings", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as EcommerceConnectionOutletMapping[]).reduce((acc: Array<string>, curr: EcommerceConnectionOutletMapping) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "fallbackOutletId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceConnectionSettings.FallbackOutletId cannot be empty"},
            {field: "shippingMappings", rule: (v: any) => !!v || "shippingMappings is required"},
            {field: "shippingMappings", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as EcommerceConnectionShippingMapping[]).reduce((acc: Array<string>, curr: EcommerceConnectionShippingMapping) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class TenantSettingsId extends ModelObject<TenantSettingsId> {

    public tenantId: Guid = Guid.createEmpty();
    public ecommerceConnectionSettings: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.ecommerceConnectionSettings = Guid.parseOrEmpty(jsonSerializedObject.ecommerceConnectionSettings);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.ecommerceConnectionSettings = this.ecommerceConnectionSettings.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.TenantId cannot be empty"},
            {field: "ecommerceConnectionSettings", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.EcommerceConnectionSettings cannot be empty"},
    ];
}


export class IntegrateOnceDetails extends ModelObject<IntegrateOnceDetails> {

    public ecommerceConnectionSettingsId: Guid = Guid.createEmpty();
    public integrateOnceEcommerceIntegrationId: Guid = Guid.createEmpty();
    public integrateOnceCustomerId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ecommerceConnectionSettingsId = Guid.parseOrEmpty(jsonSerializedObject.ecommerceConnectionSettingsId);
          this.integrateOnceEcommerceIntegrationId = Guid.parseOrEmpty(jsonSerializedObject.integrateOnceEcommerceIntegrationId);
          this.integrateOnceCustomerId = Guid.parseOrEmpty(jsonSerializedObject.integrateOnceCustomerId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ecommerceConnectionSettingsId = this.ecommerceConnectionSettingsId.toString();
        toRet.integrateOnceEcommerceIntegrationId = this.integrateOnceEcommerceIntegrationId.toString();
        toRet.integrateOnceCustomerId = this.integrateOnceCustomerId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ecommerceConnectionSettingsId", rule: (v: any) => !v.isEmpty() || "IntegrateOnceDetails.EcommerceConnectionSettingsId cannot be empty"},
            {field: "integrateOnceEcommerceIntegrationId", rule: (v: any) => !v.isEmpty() || "IntegrateOnceDetails.IntegrateOnceEcommerceIntegrationId cannot be empty"},
            {field: "integrateOnceCustomerId", rule: (v: any) => !v.isEmpty() || "IntegrateOnceDetails.IntegrateOnceCustomerId cannot be empty"},
    ];
}


export class EcommerceConnectionSettingsService extends ServiceBase {

  public async getEcommerceConnectionSettings(ecommerceConnectionSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EcommerceConnectionSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecommerceConnectionSettingsId:ecommerceConnectionSettingsId.toString(),
    };
    const extractor = (response:any) => new EcommerceConnectionSettings(response.ecommerceConnectionSettings);;
    return this.makeJsonRequest("ecommerceConnectionSettings/v1/getEcommerceConnectionSettings", req, false, extractor, opts);
  };

  public async getAllEcommerceConnectionSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EcommerceConnectionSettings)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.ecommerceConnectionSettings.map((v: any) => new EcommerceConnectionSettings(v));;
    return this.makeJsonRequest("ecommerceConnectionSettings/v1/getAllEcommerceConnectionSettings", req, false, extractor, opts);
  };

  public async getEcommerceConnectionSettingsForStoreName(originType: EnumEcommerceOrderOriginType, storeName: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EcommerceConnectionSettings | null> {
    const req = {
    tenantId:api.tenantId.toString(),
    originType:originType.value,
    storeName:storeName,
    };
    const extractor = (response:any) => response.ecommerceConnectionSettings == null ? null : new EcommerceConnectionSettings(response.ecommerceConnectionSettings);;
    return this.makeJsonRequest("ecommerceConnectionSettings/v1/getEcommerceConnectionSettingsForStoreName", req, false, extractor, opts);
  };

  public async disconnectEcommerceConnection(ecommerceConnectionSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecommerceConnectionSettingsId:ecommerceConnectionSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("ecommerceConnectionSettings/v1/disconnectEcommerceConnection", req, true, extractor, opts);
  };

  public async saveEcommerceConnectionSettings(ecommerceConnectionSettings: EcommerceConnectionSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EcommerceConnectionSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecommerceConnectionSettings:ecommerceConnectionSettings.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new EcommerceConnectionSettings(response.ecommerceConnectionSettings);;
    return this.makeJsonRequest("ecommerceConnectionSettings/v1/saveEcommerceConnectionSettings", req, true, extractor, opts);
  };

  public async hardDeleteEcommerceConnectionSettings(ecommerceConnectionSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecommerceConnectionSettingsId:ecommerceConnectionSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("ecommerceConnectionSettings/v1/hardDeleteEcommerceConnectionSettings", req, true, extractor, opts);
  };
}

export const ecommerceConnectionSettingsService = new EcommerceConnectionSettingsService();